import { useState } from 'react';

import axios from 'axios';
import toast from 'react-hot-toast';
import { t } from 'i18next';
import {
    useDispatch,
    useSelector
} from 'react-redux';

import styles from './PlanUi.module.css';
import BubbleButton from '../../Buttons/BubbleButton/BubbleButton';
import { applyDiscount } from '../../../../services/paymentService';
import { fetchUserByToken, fetchUserByTokenWithoutLoading } from '../../../../app/features/userSlice';
import { Typography } from '../../../Typography/typography';
import { useNavigate } from 'react-router-dom';
import GlobalModal from '../../Popups/GlobalModal';

const PlanUi = ({ price, onSeeAllPlans, subscriptionId, interval_count, discount, autoRenewalStatus, status, planName, handleCancelSubscription, handleResumeSubscription, current_period_end, start_date, default_payment_method, interval }) => {
    const [autoRenewal, setAutoRenewal] = useState(autoRenewalStatus);
    const [showConfirmation, setShowConfirmation] = useState(false);

    const handleAutoRenewal = async () => {
        try {
            // First make the API call to update auto renewal
            const response = await axios.post(`${process.env.REACT_APP_API_URL}payment/updateAutoRenewal`, {
                subscriptionId,
                autoRenewal: !autoRenewal
            });
            
            // Wait for the user data to be fetched before resolving the promise
            await dispatch(fetchUserByTokenWithoutLoading(userToken));
            
            // Only return after user data is refreshed
            return response.data;
        } catch (error) {
            throw error.response.data;
        }
    }

    const handleToggle = async () => {
        if (status == 'active') {
            if (autoRenewal) {
                // If trying to disable auto-renewal, show confirmation popup
                setShowConfirmation(true);
            } else {
                // If enabling auto-renewal, proceed directly
                setAutoRenewal(prevState => !prevState);
                toast.promise(handleAutoRenewal(), {
                    loading: t('general.UI.loading'),
                    success: <b>{t('billing.autoRenewalActive')}</b>,
                    error: (err) => {
                        return <b>{t('general.UI.something_went_wrong')}</b>;
                    },
                });
            }
        }
    };

    const handleConfirmDisable = async () => {
        setAutoRenewal(prevState => !prevState);
        setShowConfirmation(false);
        toast.promise(handleAutoRenewal(), {
            loading: t('general.UI.loading'),
            success: <b>{t('billing.autoRenewalDeactive')}</b>,
            error: (err) => {
                return <b>{t('general.UI.something_went_wrong')}</b>;
            },
        });
    };

    const handleCancelDisable = () => {
        setShowConfirmation(false);
    };

    const dateConverter = (value) => {
        const timestamp = value;
        const date = new Date(timestamp * 1000);
        const options = { year: 'numeric', month: 'short', day: 'numeric' };

        const formattedDate = date.toLocaleDateString('en-US', options);
        return formattedDate
    }
    const billingData = [
        { name: t('general.UI.started'), value: planName == t('planUI.FREE') ? start_date : dateConverter(start_date) },
        planName !== t('planUI.FREE') && autoRenewal && {
            name: t('billing.nextBillingDate'),
            value: dateConverter(current_period_end) + (discount ? ` (${discount})` : '')
        },
        { name: t('billing.paymentMethod'), value: default_payment_method },
        { name: t('billing.expires'), value: planName == t('planUI.FREE') ? current_period_end : dateConverter(current_period_end) },
    ].filter(Boolean);

    const user = useSelector(state => state.user.user);
    const userToken = useSelector(state => state.user.token);

    const dispatch = useDispatch();

    const [loading, setLoading] = useState(false);

  

    return (
        <div className={styles.planContainer}>
            <GlobalModal
                show={showConfirmation}
                onClose={handleCancelDisable}
                title={t('billing.confirmDisableAutoRenewal')}
                message={t('billing.confirmDisableAutoRenewalMessage')}
                primaryButtonText={t('general.UI.yes')}
                secondaryButtonText={t('general.UI.no')}
                primaryButtonAction={handleConfirmDisable}
                secondaryButtonAction={handleCancelDisable}
            />
            <div className={styles.currentPlanHeader}>
                <p className={styles.planHeading}>{t('billing.currentPlan')}</p>
                {planName !== t('planUI.FREE') && status == 'active' &&

                    <div className={styles.toggleContainer}>
                        <span className={styles.autoRenewal}>{t('billing.autoRenewal')}</span>
                        <label className={styles.switch}>
                            <input type="checkbox" checked={autoRenewal} onChange={handleToggle} />
                            <span className={`${styles.slider} ${styles.round}`}></span>
                        </label>
                    </div>
                }

            </div>
            <div className={styles.selectedPlanContainer}>
                <div className={styles.planHeader}>
                    <div className={styles.renewal}>{planName?.length > 0 ? planName : t('billing.planName')}</div>
                    <div className={styles.month}>{discount ? '$' + price + ` ${t('general.UI.nextRenewal')}` : ('$' + price + '/' + interval_count + interval?.slice(0, 2))}</div >
                </div>
                <div className={`${styles.activeContainer} ${styles.activeLink}`}>
                    {status == 'active' ?
                        <span>{t('billing.activeStatus')}</span>
                        :
                        <span className={styles.notActive} >{status}</span>
                    }
                </div>
            </div>
            <div className={styles.dataList} style={{ display: "flex", flexDirection: "row" }}>
                <div style={{ display: "flex", gap: '20px', width: "60%", flexDirection: "column" }}>
                    {billingData.map((item, index) => (
                        <div key={index} className={styles.data} style={{ width: "100%" }}>
                            <p className={styles.name}>{item.name}</p>
                            <div className={styles.valueContainer}>
                                <p className={styles.value}>{item.value}</p>
                            </div>
                        </div>
                    ))}

                </div>

                <div className={styles.planCancelRequestContainer} style={{
                    border: user?.subscriptionStatus == 'active' ? "1px solid rgba(2, 3, 6, .1)" : planName == t('planUI.FREE') ? "1px solid rgba(2, 3, 6, .1)" : user.discountApplied ? '0px solid black' : autoRenewal ? "0px solid rgba(2, 3, 6, .1)" : "1px solid rgba(2, 3, 6, .1)",
                }}>
                    {planName == t('planUI.FREE') ?
                        <>
                            <p className={styles.cancel} style={{ margin: '0px', }}>{t('pricing.upgrade_now_premium')}</p>
                            <BubbleButton onClick={onSeeAllPlans} dark headerDarkButton style={{ textTransform: "capitalize" }}>{t('pricing.upgrade')}</BubbleButton>
                        </>
                        :
                        status === 'paused' ?
                        <>
                            <Typography title={t('subscription.paused_status', 'Your subscription is currently paused.')}
                                fontSize={16}
                                weight={400}
                            />
                            <BubbleButton onClick={handleResumeSubscription} dark headerDarkButton style={{ textTransform: "capitalize" }}>
                                {t('subscription.resume_button', 'Resume Subscription')}
                            </BubbleButton>
                        </>
                        :
                        user?.subscriptionStatus == 'active' && <>
                            <Typography title={'You have access to all the premium features.'}
                                fontSize={16}
                                weight={400}
                            />
                            <BubbleButton onClick={onSeeAllPlans} dark headerDarkButton style={{ textTransform: "capitalize" }}>{'Features List'}</BubbleButton>
                        </>
                    }

                </div>
            </div>
            <div
                className={`${styles.data} ${styles.cancelSubscription}`} style={{ width: '100%' }}>
                {
                    planName == t('planUI.FREE') ? <></> :
                        user.account_type == 'employer' ?
                            autoRenewal ?
                                status === 'active' ?
                                    <p onClick={handleCancelSubscription} className={styles.cancel}>{t('billing.cancelSubscription')}</p>
                                    :
                                    <p className={styles.cancelled}>{t('employer.subscription_form.cancelled')}</p>
                                :
                                <p className={styles.cancel}>{t('Subscription_will_be_cancelled_at_the_end_of_billing_period')} ( {dateConverter(current_period_end)} )</p>
                            :
                            autoRenewal ?
                                status === 'active' ?
                                    <p onClick={handleCancelSubscription} className={styles.cancel}>{t('billing.cancelSubscription')}</p>
                                    :
                                    <p className={styles.cancelled}>{t('employer.subscription_form.cancelled')}</p> :
                                <p className={styles.cancel}>{t('Subscription_will_be_cancelled_at_the_end_of_billing_period')} ( {dateConverter(current_period_end)} )</p>

                }
            </div>
        </div>
    );
};

export default PlanUi;
